import { useNowContext, useReminders, useServices, useTasks } from "@hq/client-react";
import { dailyAgendaForDate, type DailyReminder } from "@hq/core";
import { DailyNotes } from "@hq/editor";
import type { PaneComponentProps } from "@hq/layout";
import { Checkbox, cn, toast } from "@z/ui";
import { Bell, Clock } from "lucide-react";
import { useWorkspace } from "../workspace/WorkspaceContext";

const timeFormats = new Map<string, Intl.DateTimeFormat>();
function agendaTime(at: string, timeZone: string) {
  let format = timeFormats.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit", timeZone });
    timeFormats.set(timeZone, format);
  }
  return format.format(new Date(at));
}

/** A day can have an agenda before a journal note exists. */
export function DailyPane({ pane, focused }: PaneComponentProps) {
  const { data: tasks } = useTasks();
  const { data: reminders } = useReminders();
  const now = useNowContext();
  return (
    <DailyNotes
      autoFocus={focused && pane.params.autoFocus === true}
      className="h-full"
      renderDayFooter={(date, note) => (
        <DailyAgenda
          date={date}
          agenda={dailyAgendaForDate(date, tasks ?? [], reminders ?? [], now.timeZone, note?.id)}
          timeZone={now.timeZone}
        />
      )}
    />
  );
}

function ReminderTime({ entry, timeZone }: { entry: DailyReminder; timeZone: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-fg-faint"
      title={entry.reminder.title}
    >
      <Bell aria-hidden="true" className="size-3" />
      <time dateTime={entry.at}>{agendaTime(entry.at, timeZone)}</time>
    </span>
  );
}

function DailyAgenda({
  date,
  agenda,
  timeZone,
}: {
  date: string;
  agenda: ReturnType<typeof dailyAgendaForDate>;
  timeZone: string;
}) {
  const services = useServices();
  const ws = useWorkspace();
  if (!agenda.tasks.length && !agenda.reminders.length) return null;
  return (
    <ul
      aria-label={`Tasks and reminders for ${date}`}
      className="space-y-0.5 border-t border-border/30 pt-2"
    >
      {agenda.tasks.map(({ task, due, planned, scheduled, reminders }) => (
        <li
          key={task.id}
          className="flex min-h-10 items-start gap-2 rounded-md px-1 text-sm text-fg-muted hover:bg-fg/3"
        >
          <span className="flex h-10 shrink-0 items-center">
            <Checkbox
              variant="task"
              aria-label={`${task.status === "completed" ? "Reopen" : "Complete"} “${task.title}”`}
              checked={task.status === "completed"}
              onCheckedChange={() =>
                void services.tasks
                  .toggleComplete(task.id)
                  .catch(() => toast({ title: "Could not update task", variant: "danger" }))
              }
              className="relative size-3.5 after:absolute after:-inset-x-1 after:-inset-y-3"
            />
          </span>
          <button
            type="button"
            onClick={() => ws.openTask(task.id)}
            className={cn(
              "min-h-10 min-w-0 flex-1 py-2 text-left break-words hover:text-fg focus-ring",
              task.status === "completed" && "text-fg-faint line-through",
            )}
          >
            {task.title}
          </button>
          <span className="flex min-h-10 max-w-[45%] flex-wrap items-center justify-end gap-x-2 gap-y-1 py-2">
            {planned && <span className="text-xs text-fg-faint">Planned</span>}
            {due && <span className="text-xs text-fg-faint">Due</span>}
            {scheduled && task.scheduledAt && (
              <span
                title="Scheduled time"
                className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-fg-faint"
              >
                <Clock aria-hidden="true" className="size-3" />
                <time dateTime={task.scheduledAt}>{agendaTime(task.scheduledAt, timeZone)}</time>
              </span>
            )}
            {reminders.map((entry) => (
              <ReminderTime key={entry.reminder.id} entry={entry} timeZone={timeZone} />
            ))}
          </span>
        </li>
      ))}
      {agenda.reminders.map((entry) => (
        <li
          key={entry.reminder.id}
          className="flex min-h-10 items-start gap-2 rounded-md px-1 text-sm text-fg-muted"
        >
          <Bell aria-hidden="true" className="mt-3 size-3.5 shrink-0 text-fg-faint" />
          {entry.reminder.target.type === "note" ? (
            <button
              type="button"
              onClick={() => {
                if (entry.reminder.target.type === "note") ws.openNote(entry.reminder.target.id);
              }}
              className="min-h-10 min-w-0 flex-1 py-2 text-left break-words hover:text-fg focus-ring"
            >
              {entry.reminder.title}
            </button>
          ) : (
            <span className="min-w-0 flex-1 py-2 break-words">{entry.reminder.title}</span>
          )}
          <time dateTime={entry.at} className="shrink-0 py-2.5 text-xs text-fg-faint">
            {agendaTime(entry.at, timeZone)}
          </time>
        </li>
      ))}
    </ul>
  );
}
